import * as XLSX from 'xlsx-js-style';
import { Customer, TransactionRecord } from '@/types/customer';
import { InvoiceResponse } from '@/types/invoice';
import { ReceiptVoucherResponse } from '@/types/receiptVoucher';
import { ExcelCellStyle, ExcelBorderStyle, ExcelAlignmentStyle, ExcelBorderConfig } from '@/types/excel';

interface ExportOptions {
  customer: Customer;
  transactions: TransactionRecord[];
  invoiceDetailsMap: Map<string, InvoiceResponse>;
  receiptDetailsMap: Map<string, ReceiptVoucherResponse>;
  dateFrom: string;
  dateTo: string;
  openingBalance: number;
  closingBalance: number;
  invoiceAmount: number;
  receiptAmount: number;
  t: (key: string) => string;
}

/**
 * Generates Excel file with formatted customer debt report from nested table
 */
export class CustomerDebtNestedTableExport {
  private options: ExportOptions;
  private worksheet: XLSX.WorkSheet;
  private currentRow: number;
  private headerRowStart: number = 0;
  private dataRowStart: number = 0;
  private dataRowEnd: number = 0;
  private companyRowNumber: number = 0;
  private titleRowNumber: number = 0;
  private dateRangeRowNumber: number = 0;
  private boldRows: Set<number> = new Set();

  constructor(options: ExportOptions) {
    this.options = options;
    this.worksheet = {};
    this.currentRow = 1;
    this.boldRows = new Set();
  }

  /**
   * Generates Excel file with formatted customer debt report
   */
  public async generateExcel(): Promise<Blob> {
    // Initialize worksheet
    this.worksheet = {};
    this.currentRow = 1;

    // Add fixed header rows (3 rows)
    this.addFixedHeaders();

    // Add title row with file name and date range
    this.addTitleRow();

    // Add customer information
    this.addCustomerInfo();

    // Add detailed debt table
    this.addDebtTable();

    // Add signature section
    this.addSignatureSection();

    // Apply formatting after all content is added
    this.applyFormatting();

    // Ensure worksheet has proper range (now up to column K)
    if (Object.keys(this.worksheet).length > 0) {
      const range = XLSX.utils.decode_range(this.worksheet['!ref'] || 'A1:K' + this.currentRow);
      this.worksheet['!ref'] = XLSX.utils.encode_range(range);
    } else {
      this.worksheet['!ref'] = 'A1:K1';
    }

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, this.worksheet, 'Customer Debt Report');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    return blob;
  }

  private addFixedHeaders(): void {
    // Row 1: Company name - store row number for formatting
    this.companyRowNumber = this.currentRow;
    this.setCellValue('A', this.currentRow, 'phuongnamprolab');
    this.currentRow++;

    // Row 2: Address
    this.setCellValue('A', this.currentRow, this.options.t('customers.debtExport.address'));
    this.setCellValue('B', this.currentRow, 'Quận Hai Bà Trưng, Hà Nội');
    this.currentRow++;

    // Row 3: Phone
    this.setCellValue('A', this.currentRow, this.options.t('customers.debtExport.phone'));
    this.setCellValue('B', this.currentRow, '0915241818');
    this.currentRow++;
  }

  /**
   * Adds title row with merged cells and center alignment for title and date range
   */
  private addTitleRow(): void {
    this.currentRow++; // Empty row

    // Title row - merge cells A to K and apply formatting
    const title = this.options.t('customers.debtExport.reportTitle');
    this.titleRowNumber = this.currentRow;
    this.setCellValue('A', this.titleRowNumber, title);

    // Add merge range for title row (A to K)
    if (!this.worksheet['!merges']) {
      this.worksheet['!merges'] = [];
    }
    this.worksheet['!merges'].push({
      s: { c: 0, r: this.titleRowNumber - 1 }, // Start: column A (0), row (0-based)
      e: { c: 10, r: this.titleRowNumber - 1 }  // End: column K (10), row (0-based)
    });

    this.currentRow++;

    // Date range row - merge cells A to K and apply formatting
    const dateRange = `${this.options.t('customers.debtExport.from')} ${this.formatDate(this.options.dateFrom)} ${this.options.t('customers.debtExport.to')} ${this.formatDate(this.options.dateTo)}`;
    this.dateRangeRowNumber = this.currentRow;
    this.setCellValue('A', this.dateRangeRowNumber, dateRange);

    // Add merge range for date range row (A to K)
    this.worksheet['!merges'].push({
      s: { c: 0, r: this.dateRangeRowNumber - 1 }, // Start: column A (0), row (0-based)
      e: { c: 10, r: this.dateRangeRowNumber - 1 }  // End: column K (10), row (0-based)
    });

    this.currentRow += 2; // Extra spacing
  }

  private addCustomerInfo(): void {
    const { customer } = this.options;

    // Customer name
    this.setCellValue('A', this.currentRow, this.options.t('customers.fields.name') + ':');
    this.setCellValue('B', this.currentRow, customer.name);
    this.setCellValue('F', this.currentRow, this.options.t('customers.debtExport.initialDebt') + ':');
    this.setCellValueWithFormat('G', this.currentRow, this.options.openingBalance, 'currency');
    this.currentRow++;

    // Customer code
    this.setCellValue('A', this.currentRow, this.options.t('customers.fields.code') + ':');
    this.setCellValue('B', this.currentRow, customer.code);
    this.setCellValue('F', this.currentRow, this.options.t('customers.debtExport.periodDebt') + ':');
    this.setCellValueWithFormat('G', this.currentRow, this.options.invoiceAmount, 'currency');
    this.currentRow++;

    // Phone
    this.setCellValue('A', this.currentRow, this.options.t('customers.fields.phone') + ':');
    this.setCellValue('B', this.currentRow, customer.phone || '');
    this.setCellValue('F', this.currentRow, this.options.t('customers.debt.receiptAmount') + ':');
    this.setCellValueWithFormat('G', this.currentRow, this.options.receiptAmount, 'currency');
    this.currentRow++;

    // End debt row
    this.setCellValue('F', this.currentRow, this.options.t('customers.debtExport.endDebt') + ':');
    this.setCellValueWithFormat('G', this.currentRow, this.options.closingBalance, 'currency');
    this.currentRow += 2; // Extra spacing
  }

  private addDebtTable(): void {
    // Store header row position for formatting
    this.headerRowStart = this.currentRow;

    // Table headers
    const headers = [
      this.options.t('customers.debtExport.time'),
      this.options.t('customers.debtExport.code'),
      this.options.t('customers.debtExport.description'),
      this.options.t('customers.debtExport.unit'),
      this.options.t('customers.debtExport.quantity'),
      this.options.t('customers.debtExport.unitPrice'),
      this.options.t('customers.debtExport.amount'),
      // New transaction note column (Ghi chú) right after amount
      this.options.t('customers.debtExport.transactionNote'),
      // Existing debit/credit columns
      this.options.t('customers.debtExport.note'),
      this.options.t('customers.debtExport.memo'),
      // Service notes column (Ghi chú dịch vụ)
      this.options.t('customers.debtExport.serviceNotes')
    ];

    // Add header row
    headers.forEach((header, index) => {
      this.setCellValue(this.getColumnLetter(index), this.currentRow, header);
    });
    this.currentRow++;

    // Store data start position
    this.dataRowStart = this.currentRow;

    // Add transaction records to table
    this.options.transactions.forEach(transaction => {
      this.addTransactionRow(transaction);
    });

    // Store data end position
    this.dataRowEnd = this.currentRow - 1;
  }

  private addTransactionRow(transaction: TransactionRecord): void {
    const formattedDate = this.formatDate(transaction.created_at);
    
    // Description based on record type
    if (transaction.record_type === 'debt_adjustment') {
      // Debt adjustment row (will be bolded)
      const adjustmentMainRow = this.currentRow;
      this.setCellValueWithWrapText('A', this.currentRow, formattedDate);
      this.setCellValue('B', this.currentRow, transaction.code);
      this.setCellValue('C', this.currentRow, this.options.t('debtAdjustmentVoucher.titleSingle'));
      
      // Transaction note column (H) - use adjustment reason
      this.setCellValue('H', this.currentRow, transaction.reason || '');
      
      // Adjustment amount: negative goes to "Ghi có" (J), positive goes to "Ghi nợ" (I)
      const adjustmentAmount = transaction.adjustment_amount || 0;
      if (adjustmentAmount < 0) {
        // Negative adjustment (decrease debt) goes to "Ghi có" column (J)
        this.setCellValueWithFormat('J', this.currentRow, Math.abs(adjustmentAmount), 'currency');
      } else {
        // Positive adjustment (increase debt) goes to "Ghi nợ" column (I)
        this.setCellValueWithFormat('I', this.currentRow, adjustmentAmount, 'currency');
      }
      
      // Mark this row for bold formatting
      this.markRowForBoldFormatting(adjustmentMainRow);
      
      this.currentRow++;
    } else if (transaction.record_type === 'invoice') {
      // Get invoice details if available
      const invoiceDetail = this.options.invoiceDetailsMap.get(transaction.id);
      
      // Add invoice header row (main row - will be bolded)
      const invoiceMainRow = this.currentRow;
      this.setCellValueWithWrapText('A', this.currentRow, formattedDate);
      this.setCellValue('B', this.currentRow, transaction.code);
      this.setCellValue('C', this.currentRow, 'Bán Hàng'); // Changed to "Bán Hàng"
      // Transaction note column (H) - use invoice note (not line notes)
      this.setCellValue('H', this.currentRow, invoiceDetail?.note || '');
      // Invoice amount goes to "Ghi nợ" column (I)
      this.setCellValueWithFormat('I', this.currentRow, transaction.total || 0, 'currency');
      // Service notes column (K) - collect notes from all invoice lines
      const serviceNotes = this.collectServiceNotes(invoiceDetail);
      this.setCellValueWithWrapText('K', this.currentRow, serviceNotes);
      
      // Mark this row for bold formatting
      this.markRowForBoldFormatting(invoiceMainRow);
      
      this.currentRow++;
      
      // Add invoice lines if available
      if (invoiceDetail && invoiceDetail.invoice_lines && invoiceDetail.invoice_lines.length > 0) {
        invoiceDetail.invoice_lines.forEach((line) => {
          this.setCellValue('B', this.currentRow, line.code || '');
          this.setCellValue('C', this.currentRow, line.service_name || '');
          this.setCellValue('D', this.currentRow, ''); // Unit not available in InvoiceLineResponse
          this.setCellValueWithFormat('E', this.currentRow, line.quantity || 0, 'number');
          this.setCellValueWithFormat('F', this.currentRow, line.selling_price || line.unit_price || 0, 'currency');
          this.setCellValueWithFormat('G', this.currentRow, line.total_price || 0, 'currency');
          this.currentRow++;
        });
      }
    } else if (transaction.record_type === 'receipt') {
      // Receipt voucher row (will be bolded)
      const receiptDetail = this.options.receiptDetailsMap.get(transaction.code);
      const receiptMainRow = this.currentRow;
      
      // Build description with payment method
      let description = this.options.t('customers.debt.receiptAmount');
      let paymentMethodLabel = '';
      if (receiptDetail && receiptDetail.payment_method) {
        paymentMethodLabel = this.getPaymentMethodLabel(receiptDetail.payment_method);
      }
      
      this.setCellValueWithWrapText('A', this.currentRow, formattedDate);
      this.setCellValue('B', this.currentRow, transaction.code);
      
      // Set cell value with line break in Description column (C)
      const cellAddress = `C${this.currentRow}`;
      if (paymentMethodLabel) {
        // Create multi-line text using line break character (char code 10)
        const multiLineText = description + String.fromCharCode(10) + paymentMethodLabel;
        
        this.worksheet[cellAddress] = { 
          v: multiLineText,
          t: 's',
          s: {
            alignment: { 
              wrapText: true,
              vertical: 'top',
              horizontal: 'left'
            }
          }
        };
      } else {
        this.worksheet[cellAddress] = { 
          v: description,
          t: 's'
        };
      }
      this.updateWorksheetRange(cellAddress);

      // Transaction note column (H) - use receipt voucher note
      this.setCellValue('H', this.currentRow, receiptDetail?.note || '');
      
      // Receipt amount goes to "Ghi có" column (J)
      this.setCellValueWithFormat('J', this.currentRow, transaction.received_now || 0, 'currency');
      
      // Mark this row for bold formatting
      this.markRowForBoldFormatting(receiptMainRow);
      
      this.currentRow++;
    } else if (transaction.record_type === 'discount_voucher') {
      // Discount voucher row (will be bolded)
      const discountMainRow = this.currentRow;
      this.setCellValueWithWrapText('A', this.currentRow, formattedDate);
      this.setCellValue('B', this.currentRow, transaction.code);
      this.setCellValue('C', this.currentRow, this.options.t('discountVoucher.titleSingle'));
      
      // Transaction note column (H) - use discount reason
      this.setCellValue('H', this.currentRow, transaction.reason || '');
      
      // Discount amount goes to "Ghi có" column (J) - decreases debt
      this.setCellValueWithFormat('J', this.currentRow, transaction.total_discount || 0, 'currency');
      
      // Mark this row for bold formatting
      this.markRowForBoldFormatting(discountMainRow);
      
      this.currentRow++;
    }
  }

  private getPaymentMethodLabel(paymentMethod: string): string {
    const methodMap: Record<string, string> = {
      'cash': this.options.t('receiptVoucher.paymentMethod.cash') || 'Tiền mặt',
      'bank_transfer': this.options.t('receiptVoucher.paymentMethod.bankTransfer') || 'Chuyển khoản',
      'card': this.options.t('receiptVoucher.paymentMethod.card') || 'Thẻ',
      'other': this.options.t('receiptVoucher.paymentMethod.other') || 'Khác',
    };
    return methodMap[paymentMethod] || paymentMethod;
  }

  /**
   * Collects all service notes from invoice lines
   * @param invoiceDetail Invoice detail with invoice lines
   * @returns Combined notes from all invoice lines, separated by newlines
   */
  private collectServiceNotes(invoiceDetail: InvoiceResponse | undefined): string {
    if (!invoiceDetail || !invoiceDetail.invoice_lines || invoiceDetail.invoice_lines.length === 0) {
      return '';
    }

    // Filter invoice lines that have notes and collect them
    const notes = invoiceDetail.invoice_lines
      .filter(line => line.note && line.note.trim() !== '')
      .map(line => line.note.trim());

    // Combine notes with newline separator for better readability
    return notes.join(String.fromCharCode(10)); // Use newline character
  }

  private addSignatureSection(): void {
    this.currentRow += 2; // Extra spacing

    // Date
    const today = new Date();
    this.setCellValue('H', this.currentRow, `${this.options.t('customers.debtExport.date')} ${today.getDate()} ${this.options.t('customers.debtExport.month')} ${today.getMonth() + 1} ${this.options.t('customers.debtExport.year')} ${today.getFullYear()}`);
    this.currentRow += 2;

    // Signature fields
    this.setCellValue('A', this.currentRow, this.options.t('customers.debtExport.customer'));
    this.setCellValue('D', this.currentRow, this.options.t('customers.debtExport.preparedBy'));
    this.setCellValue('H', this.currentRow, this.options.t('customers.debtExport.company'));
    this.currentRow++;

    this.setCellValue('A', this.currentRow, `(${this.options.t('customers.debtExport.signAndName')})`);
    this.setCellValue('D', this.currentRow, `(${this.options.t('customers.debtExport.signAndName')})`);
    this.setCellValue('H', this.currentRow, `(${this.options.t('customers.debtExport.signAndName')})`);
  }

  private setCellValue(column: string, row: number, value: string | number): void {
    const cellAddress = `${column}${row}`;
    this.worksheet[cellAddress] = { v: value, t: typeof value === 'number' ? 'n' : 's' };

    // Update worksheet range
    this.updateWorksheetRange(cellAddress);
  }

  private setCellValueWithWrapText(column: string, row: number, value: string): void {
    const cellAddress = `${column}${row}`;
    this.worksheet[cellAddress] = { 
      v: value, 
      t: 's',
      s: {
        alignment: { 
          wrapText: true,
          vertical: 'center',
          horizontal: 'left'
        }
      }
    };

    // Update worksheet range
    this.updateWorksheetRange(cellAddress);
  }

  private setCellValueWithFormat(column: string, row: number, value: number, format: 'currency' | 'number'): void {
    const cellAddress = `${column}${row}`;
    const formattedValue = format === 'currency' ? this.formatCurrency(value) : this.formatNumber(value);

    // Store as string with Vietnamese formatting (dots for thousands)
    this.worksheet[cellAddress] = {
      v: formattedValue,
      t: 's' // String type to preserve Vietnamese formatting
    };

    // Update worksheet range
    this.updateWorksheetRange(cellAddress);
  }

  private updateWorksheetRange(cellAddress: string): void {
    if (!this.worksheet['!ref']) {
      this.worksheet['!ref'] = `${cellAddress}:${cellAddress}`;
    } else {
      const range = XLSX.utils.decode_range(this.worksheet['!ref']);
      const cellRef = XLSX.utils.decode_cell(cellAddress);

      if (cellRef.c < range.s.c) range.s.c = cellRef.c;
      if (cellRef.c > range.e.c) range.e.c = cellRef.c;
      if (cellRef.r < range.s.r) range.s.r = cellRef.r;
      if (cellRef.r > range.e.r) range.e.r = cellRef.r;

      this.worksheet['!ref'] = XLSX.utils.encode_range(range);
    }
  }

  private getColumnLetter(index: number): string {
    return String.fromCharCode(65 + index); // A, B, C, etc.
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return '';
      }
      // Format date part
      const datePart = date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      // Format time part
      const timePart = date.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      
      // Combine with line break (char code 10)
      return datePart + String.fromCharCode(10) + timePart;
    } catch (error) {
      return '';
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }

  /**
   * Applies Excel formatting including column widths, header styling, borders, and alignment
   */
  private applyFormatting(): void {
    // Set column widths for better readability
    this.worksheet['!cols'] = [
      { wch: 10 }, // A - Time
      { wch: 12 }, // B - Code
      { wch: 24 }, // C - Description
      { wch: 10 }, // D - Unit
      { wch: 10 }, // E - Quantity
      { wch: 15 }, // F - Unit Price
      { wch: 12 }, // G - Amount
      { wch: 20 }, // H - Transaction Note (Ghi chú)
      { wch: 12 }, // I - Debit (Ghi nợ)
      { wch: 20 }, // J - Credit (Ghi có)
      { wch: 30 }, // K - Service Notes (Ghi chú dịch vụ)
    ];

    // Apply company name formatting
    this.applyCompanyNameFormatting();

    // Apply title row formatting
    this.applyTitleFormatting();

    // Apply header row formatting if we have a header
    if (this.headerRowStart > 0) {
      this.applyHeaderFormatting();
    }

    // Apply data row formatting if we have data
    if (this.dataRowStart > 0 && this.dataRowEnd >= this.dataRowStart) {
      this.applyDataFormatting();
    }

    // Apply borders to the entire table
    if (this.headerRowStart > 0 && this.dataRowEnd >= this.headerRowStart) {
      this.applyTableBorders();
    }
  }

  /**
   * Applies bold formatting to company name
   */
  private applyCompanyNameFormatting(): void {
    if (this.companyRowNumber === 0) return;

    const cellAddress = `A${this.companyRowNumber}`;
    if (this.worksheet[cellAddress]) {
      // Ensure cell has proper structure before applying styles
      if (!this.worksheet[cellAddress].s) {
        this.worksheet[cellAddress].s = {};
      }

      const companyNameStyle: ExcelCellStyle = {
        font: { bold: true, sz: 12 }
      };
      this.worksheet[cellAddress].s = companyNameStyle;
    }
  }

  /**
   * Applies formatting to title rows with center alignment and bold formatting for title
   */
  private applyTitleFormatting(): void {
    // Apply title formatting using stored row number
    if (this.titleRowNumber > 0) {
      const cellAddress = `A${this.titleRowNumber}`;
      if (this.worksheet[cellAddress]) {
        // Ensure cell has proper structure before applying styles
        if (!this.worksheet[cellAddress].s) {
          this.worksheet[cellAddress].s = {};
        }

        const titleStyle: ExcelCellStyle = {
          font: { bold: true, sz: 14 },
          alignment: { horizontal: "center", vertical: "center" }
        };
        this.worksheet[cellAddress].s = titleStyle;
      }
    }

    // Apply date range formatting using stored row number
    if (this.dateRangeRowNumber > 0) {
      const cellAddress = `A${this.dateRangeRowNumber}`;
      if (this.worksheet[cellAddress]) {
        // Ensure cell has proper structure before applying styles
        if (!this.worksheet[cellAddress].s) {
          this.worksheet[cellAddress].s = {};
        }

        const dateRangeStyle: ExcelCellStyle = {
          alignment: { horizontal: "center", vertical: "center" }
        };
        this.worksheet[cellAddress].s = dateRangeStyle;
      }
    }
  }

  /**
   * Applies formatting to header row
   */
  private applyHeaderFormatting(): void {
    for (let col = 0; col < 11; col++) {
      const cellAddress = `${this.getColumnLetter(col)}${this.headerRowStart}`;
      if (this.worksheet[cellAddress]) {
        const borderStyle: ExcelBorderStyle = { style: "thin", color: { rgb: "000000" } };
        const headerStyle: ExcelCellStyle = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { patternType: "solid", fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: borderStyle,
            bottom: borderStyle,
            left: borderStyle,
            right: borderStyle
          }
        };
        this.worksheet[cellAddress].s = headerStyle;
      }
    }
  }

  /**
   * Marks a row to be formatted with bold font
   */
  private markRowForBoldFormatting(row: number): void {
    this.boldRows.add(row);
  }

  /**
   * Applies formatting to data rows
   */
  private applyDataFormatting(): void {
    for (let row = this.dataRowStart; row <= this.dataRowEnd; row++) {
      for (let col = 0; col < 11; col++) {
        const cellAddress = `${this.getColumnLetter(col)}${row}`;
        if (this.worksheet[cellAddress]) {
          // Preserve existing style if any
          const existingStyle = this.worksheet[cellAddress].s || {};
          const existingAlignment = existingStyle.alignment || {};
          const existingFont = existingStyle.font || {};
          
          // Determine alignment based on column content
          const alignment: ExcelAlignmentStyle = { 
            vertical: existingAlignment.vertical || "center",
            wrapText: existingAlignment.wrapText // Preserve wrapText
          };

          // Right-align numeric columns (E, F, G, I, J) but keep note columns (H, K) left-aligned
          if ([4, 5, 6, 8, 9].includes(col)) {
            alignment.horizontal = "right";
          } else {
            alignment.horizontal = existingAlignment.horizontal || "left";
          }

          const dataStyle: ExcelCellStyle = {
            ...existingStyle,  // Preserve other existing styles
            alignment
          };

          // Apply bold formatting if this row is marked for bold
          if (this.boldRows.has(row)) {
            dataStyle.font = { ...existingFont, bold: true };
          }

          this.worksheet[cellAddress].s = dataStyle;
        }
      }
    }
  }

  /**
   * Applies continuous border lines to all cells in the debt table
   */
  private applyTableBorders(): void {
    const startCol = 0;
    const endCol = 10;
    const startRow = this.headerRowStart;
    const endRow = this.dataRowEnd;

    // Apply borders to all cells in the table
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cellAddress = `${this.getColumnLetter(col)}${row}`;

        // Initialize cell if it doesn't exist
        if (!this.worksheet[cellAddress]) {
          this.worksheet[cellAddress] = { v: '', t: 's' };
        }

        // Initialize style object if it doesn't exist
        if (!this.worksheet[cellAddress].s) {
          this.worksheet[cellAddress].s = {};
        }

        // Define border styles with proper typing
        const thinBorderStyle: ExcelBorderStyle = { style: "thin", color: { rgb: "000000" } };
        const mediumBorderStyle: ExcelBorderStyle = { style: "medium", color: { rgb: "000000" } };

        // Apply borders to all sides - start with thin borders
        const borderConfig: ExcelBorderConfig = {
          top: thinBorderStyle,
          bottom: thinBorderStyle,
          left: thinBorderStyle,
          right: thinBorderStyle
        };

        // Apply thicker borders to outer edges for better table definition
        if (row === startRow) {
          borderConfig.top = mediumBorderStyle;
        }
        if (row === endRow) {
          borderConfig.bottom = mediumBorderStyle;
        }
        if (col === startCol) {
          borderConfig.left = mediumBorderStyle;
        }
        if (col === endCol) {
          borderConfig.right = mediumBorderStyle;
        }

        // Apply the border configuration to the cell
        this.worksheet[cellAddress].s.border = borderConfig;
      }
    }
  }
}
